import { useMemo, useState } from "react";
import { Check, Dot } from "lucide-react";

/**
 * StageRoadmap — minimal, clickable S1–S6 recovery journey.
 *
 * Renders the six fixed rehabilitation stages as small connected nodes
 * (dots joined by a progress line), not heavy tiles. Selecting a node reveals
 * that stage's short summary + clinician rationale in the panel below.
 *
 * Read-only: patients cannot change their stage; only the clinician advances it.
 * Shown ONLY when the clinician has confirmed the S1–S6 structure (the parent
 * falls back to the legacy roadmap otherwise).
 */

// Plain-English, clinically-neutral summaries for each fixed stage.
const STAGE_SUMMARY = {
  S1: "Calm things down and protect the area while keeping gentle, pain-free movement.",
  S2: "Rebuild range of motion and basic control as the foundation for strength.",
  S3: "Build strength, including single-leg / single-side (unilateral) work.",
  S4: "Reintroduce impact and movement in multiple directions.",
  S5: "Train the specific demands of your sport or activity.",
  S6: "Review your progress together and plan the next steps.",
};

// Short one-word captions so all six nodes fit on a phone without hidden scroll.
// The full stage title is always shown in the selected-stage summary below.
const STAGE_SHORT = {
  S1: "Protect",
  S2: "Foundations",
  S3: "Strength",
  S4: "Impact",
  S5: "Sport",
  S6: "Review",
};

const STATUS_LABEL = {
  completed: "Completed",
  in_progress: "Current",
  not_started: "Upcoming",
  bypassed: "Clinically bypassed",
};

export default function StageRoadmap({ stages = [], currentStage, selectedKey: controlledKey, onSelect, embedded = false }) {
  const ordered = stages;
  const currentIdx = useMemo(
    () => ordered.findIndex((s) => s.status === "in_progress"),
    [ordered]
  );
  // Default selection = current stage, else the first stage.
  const [internalKey, setInternalKey] = useState(() => {
    const cur = ordered.find((s) => s.status === "in_progress");
    return cur?.key || ordered[0]?.key || null;
  });
  const controlled = typeof onSelect === "function";
  const selectedKey = controlled ? (controlledKey || ordered[0]?.key || null) : internalKey;
  const setSelectedKey = (k) => (controlled ? onSelect(k) : setInternalKey(k));

  if (!ordered.length) return null;

  const selected = ordered.find((s) => s.key === selectedKey) || ordered[0];

  // A node's connector/line is "filled" once that stage is reached.
  const isReached = (s) => s.status === "completed" || s.status === "in_progress";

  const dotClasses = (s, isSelected) => {
    const base =
      "relative z-10 flex items-center justify-center rounded-full border-2 transition-all duration-300";
    if (s.status === "completed")
      return `${base} w-7 h-7 bg-emerald-500 border-emerald-400 text-black`;
    if (s.status === "in_progress")
      return `${base} w-8 h-8 bg-[#f47a20] border-[#f47a20] text-black shadow-[0_0_0_5px_rgba(244,122,32,0.18)]`;
    if (s.status === "bypassed")
      return `${base} w-7 h-7 bg-zinc-700 border-zinc-600 text-zinc-400`;
    // upcoming
    return `${base} w-7 h-7 bg-transparent border-white/25 text-transparent ${
      isSelected ? "border-[#f47a20]/70" : ""
    }`;
  };

  return (
    <div className={embedded ? "" : "fl-card fl-card-elevated p-4 sm:p-6 mb-6"} data-testid="stage-roadmap">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="fl-label text-xs text-zinc-400">Recovery roadmap</div>
        <div className="text-[11px] text-zinc-500" data-testid="stage-roadmap-position">
          {currentIdx >= 0 ? `Stage ${currentIdx + 1} of ${ordered.length}` : `${ordered.length} stages`}
        </div>
      </div>

      {/* -------- Node strip (S1 ●──● S2 … S6) -------- */}
      <div className="mt-6 pb-1" data-testid="stage-roadmap-strip">
        <div className="flex">
          {ordered.map((s, i) => {
            const isSelected = s.key === selectedKey;
            const leftFilled = i > 0 && isReached(s);
            const rightFilled = i < ordered.length - 1 && isReached(ordered[i + 1]);
            return (
              <div key={s.key} className="flex-1 min-w-0 flex flex-col items-center">
                {/* dot + connectors */}
                <div className="relative flex items-center justify-center w-full h-9">
                  {i > 0 && (
                    <span
                      className={`absolute left-0 top-1/2 -translate-y-1/2 w-1/2 h-[3px] rounded-full ${
                        leftFilled ? "bg-[#f47a20]" : "bg-white/15"
                      }`}
                    />
                  )}
                  {i < ordered.length - 1 && (
                    <span
                      className={`absolute right-0 top-1/2 -translate-y-1/2 w-1/2 h-[3px] rounded-full ${
                        rightFilled ? "bg-[#f47a20]" : "bg-white/15"
                      }`}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => setSelectedKey(s.key)}
                    aria-label={`${s.key} ${s.title} — ${STATUS_LABEL[s.status] || "Upcoming"}`}
                    aria-pressed={isSelected}
                    data-testid={`stage-node-${s.key}`}
                    className={`${dotClasses(s, isSelected)} ${
                      isSelected ? "ring-2 ring-offset-2 ring-offset-[#0b0b0d] ring-[#f47a20]" : ""
                    } focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f47a20] cursor-pointer hover:scale-110`}
                  >
                    {s.status === "completed" ? (
                      <Check size={14} strokeWidth={3} />
                    ) : s.status === "in_progress" ? (
                      <Dot size={22} strokeWidth={6} />
                    ) : null}
                  </button>
                </div>
                {/* labels */}
                <button
                  type="button"
                  onClick={() => setSelectedKey(s.key)}
                  className="mt-2 w-full text-center px-0.5 cursor-pointer group"
                  data-testid={`stage-label-${s.key}`}
                >
                  <div
                    className={`text-[11px] font-bold ${
                      isSelected ? "text-[#f47a20]" : "text-zinc-300 group-hover:text-white"
                    }`}
                  >
                    {s.key}
                  </div>
                  <div
                    className={`text-[10px] leading-tight mt-0.5 ${
                      isSelected ? "text-zinc-100" : "text-zinc-400 group-hover:text-zinc-200"
                    }`}
                  >
                    {STAGE_SHORT[s.key] || shortTitle(s.title)}
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* -------- Selected stage summary -------- */}
      {selected && (
        <div
          className="mt-6 rounded-xl border border-white/10 p-4 sm:p-5 bg-white/[0.02]"
          data-testid="stage-selected-summary"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-bold text-[#f47a20]">{selected.key}</span>
                <h3 className="fl-heading text-lg sm:text-xl" data-testid="stage-selected-title">
                  {selected.title}
                </h3>
              </div>
              <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed max-w-xl">
                {STAGE_SUMMARY[selected.key] || ""}
              </p>
            </div>
            <StatusBadge status={selected.status} />
          </div>

          {selected.rationale && (
            <div className="mt-3 text-sm text-zinc-300 border-l-2 border-[#f47a20]/50 pl-3" data-testid="stage-selected-rationale">
              <span className="text-[11px] uppercase tracking-widest text-zinc-500 block mb-0.5">
                From your clinician
              </span>
              {selected.rationale}
            </div>
          )}
        </div>
      )}

      <p className="text-[11px] text-zinc-500 mt-3">
        Your clinician reviews and confirms each stage. The stage shows where you are in your
        rehabilitation, not a recovery percentage.
      </p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    completed: { label: "Completed", cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
    in_progress: { label: "Current", cls: "text-[#f8a766] border-[#f47a20]/50 bg-[#f47a20]/10" },
    bypassed: { label: "Bypassed", cls: "text-zinc-400 border-white/15 bg-white/5" },
    not_started: { label: "Upcoming", cls: "text-zinc-400 border-white/15 bg-transparent" },
  };
  const m = map[status] || map.not_started;
  return (
    <span
      className={`shrink-0 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${m.cls}`}
      data-testid="stage-selected-status"
    >
      {m.label}
    </span>
  );
}

// Trim the "S6 REVIEW" / long titles into a tidy node caption.
function shortTitle(title) {
  if (!title) return "";
  const t = title.replace(/^REVIEW$/i, "Review");
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}
